import React, { useState, useEffect } from 'react';
import useFetchWithMsal from '../../hooks/useFetchWithMsal';
import { protectedResources } from '../../authConfig';
import { ICompetencyMgmtPeriod } from '../../model/CompetencyMgmtPeriod.model';
import { useMsal } from '@azure/msal-react';

interface CompetencyPeriodSelectorProps {
  onPeriodChange(period: ICompetencyMgmtPeriod): void;
  initialPeriodId?: string;
}

const CompetencyPeriodSelector: React.FC<CompetencyPeriodSelectorProps> = ({ onPeriodChange, initialPeriodId }) => {
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiCFSkillPath.scopes.read,
  });
  const { instance } = useMsal();
  const roles = (instance.getActiveAccount()?.idTokenClaims?.roles ?? []) as string[];
  const isAdmin = roles.indexOf('CareerFramework.Admin') > -1;

  const [periods, setPeriods] = useState<ICompetencyMgmtPeriod[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');
  const [loaded, setLoaded] = useState(false);

  const selectDefaultPeriod = (periodList: ICompetencyMgmtPeriod[]): ICompetencyMgmtPeriod => {
    const requested = periodList.find(p => p.competencyMgmtPeriodId === initialPeriodId);
    if (requested) return requested;

    const now = new Date();
    const active = periodList.find(
      p => new Date(p.startDate) <= now && new Date(p.endDate) >= now
    );
    if (active) return active;

    return periodList.reduce((closest, p) => {
      const closestDiff = Math.abs(new Date(closest.startDate).getTime() - now.getTime());
      const diff = Math.abs(new Date(p.startDate).getTime() - now.getTime());
      return diff < closestDiff ? p : closest;
    });
  };

  const formatPeriodDates = (period: ICompetencyMgmtPeriod): string => {
   
    let diffYear = new Date(period.startDate).getUTCFullYear() !== new Date(period.endDate).getUTCFullYear();
    const fmt = (dateStr: string, includeYear: boolean) => {
      const d = new Date(dateStr);
      const month = d.toLocaleString('default', { month: 'long', timeZone: 'UTC' });
      const day = d.getUTCDate();
      return includeYear ? `${month} ${day}, ${d.getUTCFullYear()}` : `${month} ${day}`;
    };
    return `${fmt(period.startDate, diffYear)} to ${fmt(period.endDate, true)}`;
  };

  useEffect(() => {
    if (loaded) return;
    execute('GET', '/api/CompetencyMgmtPeriod/getAll', undefined)
      .then((response: ICompetencyMgmtPeriod[]) => {
        if (!response || response.length === 0) return;
        const defaultPeriod = selectDefaultPeriod(response);
        setPeriods(response);
        setSelectedPeriodId(defaultPeriod.competencyMgmtPeriodId);
        setLoaded(true);
        onPeriodChange(defaultPeriod);
      });
  }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const period = periods.find(p => p.competencyMgmtPeriodId === e.target.value);
    if (!period) return;
    setSelectedPeriodId(period.competencyMgmtPeriodId);
    onPeriodChange(period);
  };

  const selectedPeriod = periods.find(p => p.competencyMgmtPeriodId === selectedPeriodId);
 if (!loaded) {
    return <p>Loading competency management periods...</p>;
  }
  return (
    <div>
      {selectedPeriod && (
        <p className="fnt-wght-500 m-t-0 m-b--50">
          <span className='m-r-1'>Competency Period:</span> {selectedPeriod.periodTitle} 
          <br></br>
          <span className=''>Managers can edit these competencies from</span> {formatPeriodDates(selectedPeriod)} 
        </p>
      )}
      <label htmlFor="period-select" className='m-r-1 fnt-wght-bold'>View Period: </label>
      <select id="period-select" value={selectedPeriodId} onChange={handleChange}>
        {periods
          .filter(p => isAdmin || new Date(p.startDate) <= new Date())
          .map(p => (
            <option key={p.competencyMgmtPeriodId} value={p.competencyMgmtPeriodId}>
              {p.periodTitle}
            </option>
          ))}
      </select>
    </div>
  );
};

export default CompetencyPeriodSelector;
